import { flow, getEnv, Instance, types } from "mobx-state-tree";
import { endpoints } from "../api/ApiEndpoints";
import { FileStates, shopHistoryStorage } from "../shop/ShopHistoryStorage";
import { IServiceStorageEnv, ServiceModel } from "../app/ServiceStorage";

export const RestoreDataServiceModel = types.compose(
    types.model({
        name: types.optional(types.string, ""),
        message: types.optional(types.string, ""),
        restoredActivityType: types.optional(types.number, 0),
        restoredFileId: types.optional(types.number, 0),
        restoredGuid: types.optional(types.string, "")
    }), ServiceModel
).extend(self => ({
    actions: {
        request: flow(function* () {
            try {
                const endpoint = endpoints.get("media_file_restore_data");
                endpoint.routeParams = [self.fileId!];

                const response = yield getEnv<IServiceStorageEnv>(self).api.get(endpoint);
                self.name = response.name;
                self.isConverted = response.isRestored;
                self.status = self.getFileState(response.restoreWasSuccessful, response.restoreErrorMessage, !response.isRestored);
                self.message = response.message ?? "";
                self.restoredActivityType = response.activityType ?? 0;
                self.restoredFileId = response.fileId ?? 0;
                self.restoredGuid = response.guid ?? "";
            } catch (error) {
                self.status = FileStates.None;
            }
        }),

        runService: flow(function* () {
            try {
                const endpoint = endpoints.get("media_file_restore_data");
                endpoint.routeParams = [self.fileId!];

                // Reuse the upload session's abort signal so a user-cancel / unmount aborts the
                // long restore PUT as well, closing the connection so the server cleans up.
                const signal = getEnv<IServiceStorageEnv>(self).uploadFilesService.abortController?.signal;
                yield getEnv(self).api.put(endpoint, null, { signal });
                yield self.load();
            } catch (error: any) {
                const aborted = error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
                self.status = aborted ? FileStates.None : FileStates.Failed;
            }
        }),

        cancelRestore() {
            getEnv<IServiceStorageEnv>(self).uploadFilesService.abortUploadSession();
            self.status = FileStates.None;
        },

        startService: flow(function* () {
            if (self.fileId === undefined) return;
            yield self.runService();
        }),

        clear: () => {
            self.fileId = undefined;
            self.isConverted = false;
            self.name = "";
            self.message = "";
            self.restoredActivityType = 0;
            self.restoredFileId = 0;
            self.restoredGuid = "";
            // Reset to None (idle), NOT Failed — otherwise dismissing the failed
            // panel sets the status back to Failed and the panel re-appears.
            self.status = FileStates.None;
        },

        runAfterUpload: flow(function*() {
            yield shopHistoryStorage.load();
            yield self.startService();
        })
    }
})).views((self) => ({
    get restoreFailed(): boolean {
        return self.status === FileStates.Failed;
    },
    get restoreRunning(): boolean {
        return self.fileId !== undefined && !self.isConverted && self.status !== FileStates.Failed;
    },
    get viewDataRoute(): string {
        const guid = self.restoredGuid;
        switch (self.restoredActivityType) {
            case 1: return `/video-analysis?guid=${guid}`;
            case 2: return `/video-segmentation?guid=${guid}`;
            case 3: return `/video-conversion?guid=${guid}`;
            case 4: return `/inspection-insights?guid=${guid}`;
            case 5: return `/analysis-and-segmentation?guid=${guid}`;
            case 6: return `/analysis-and-inspection-insights?guid=${guid}`;
            default: return "";
        }
    }
}))

export type RestoreDataService = Instance<typeof RestoreDataServiceModel>;
